import type { CreateClientReviewRequest } from '@overlayqa/shared';
import { useApiQuery } from './useApiQuery';
import { useApiMutation } from './useApiMutation';
import { clientReviewsApi } from '../lib/api/clientReviews';
import { log } from '@/lib/logger';
import { useQueryClient } from '@tanstack/react-query';

log.info('[useClientReviews] Module loaded');

export function useClientReviews(projectId: string) {
  log.info('[useClientReviews] useClientReviews called', { projectId });
  return useApiQuery({
    queryKey: ['client-reviews', projectId],
    queryFn: (token) => clientReviewsApi.list(token, projectId),
    enabled: !!projectId,
    // OQA-3: visits/comments/lastSeen change server-side while the dashboard
    // sits idle — the app-default 60s staleTime served stale zeros until a
    // mutation invalidated the key. Instantly stale => every mount and window
    // refocus refetches the stats.
    staleTime: 0,
  });
}

export function useCreateClientReview() {
  log.info('[useClientReviews] useCreateClientReview called');
  return useApiMutation<
    Awaited<ReturnType<typeof clientReviewsApi.create>>,
    CreateClientReviewRequest
  >({
    mutationFn: (token, data) => clientReviewsApi.create(token, data),
    invalidateKeys: [['client-reviews']],
  });
}

/**
 * Create a TEAM review (live review) for a page, outside any modal.
 *
 * LiveReviewModal owns its own create through useApiMutation directly; this
 * exists for callers that already know the URL and have nothing to ask the
 * user, which today is the Schedule page's Capture Issues row action. Same
 * endpoint, same response envelope; only the surface differs.
 *
 * No invalidateKeys: a live review does not appear in any list this dashboard
 * caches (the client-reviews list is a different source), so invalidating
 * would be a refetch that changes nothing.
 */
export function useCreateLiveReview() {
  log.info('[useClientReviews] useCreateLiveReview called');
  return useApiMutation<
    Awaited<ReturnType<typeof clientReviewsApi.createLiveReview>>,
    { projectId: string; pageUrl: string }
  >({
    mutationFn: (token, data) => clientReviewsApi.createLiveReview(token, data),
  });
}

export function useUpdateClientReview() {
  const queryClient = useQueryClient();
  log.info('[useClientReviews] useUpdateClientReview called');
  return useApiMutation<
    Awaited<ReturnType<typeof clientReviewsApi.update>>,
    { id: string; revoke?: boolean; expiresAt?: string | null; allowResponsiveViews?: boolean }
  >({
    mutationFn: (token, { id, ...data }) => clientReviewsApi.update(token, id, data),
    invalidateKeys: [['client-reviews']],
    // Publish the acknowledged value before the checkbox leaves its pending
    // state. useApiMutation requests a refetch without waiting for its result.
    onSuccess: review => queryClient.setQueriesData<Awaited<ReturnType<typeof clientReviewsApi.list>>>(
      { queryKey: ['client-reviews'] },
      current => current?.map(row => row.id === review.id ? { ...row, ...review } : row),
    ),
  });
}

/** Replace a link's client emails (Add client emails / Edit client emails). */
export function useSetClientReviewRecipients() {
  log.info('[useClientReviews] useSetClientReviewRecipients called');
  return useApiMutation<
    Awaited<ReturnType<typeof clientReviewsApi.setRecipients>>,
    { id: string; emails: string[] }
  >({
    mutationFn: (token, { id, emails }) => clientReviewsApi.setRecipients(token, id, emails),
    invalidateKeys: [['client-reviews']],
  });
}

export function useDeleteClientReview() {
  log.info('[useClientReviews] useDeleteClientReview called');
  return useApiMutation<void, string>({
    mutationFn: (token, id) => clientReviewsApi.delete(token, id),
    invalidateKeys: [['client-reviews']],
  });
}

/**
 * Re-capture a snapshot review's page in place (same link, same comments).
 *
 * Invalidates ['client-reviews'] so the row picks up the new `capturing` status
 * without a manual refresh — the list is already configured to refetch on every
 * mount and focus (OQA-3), so the eventual flip back to active follows.
 */
export function useRecaptureClientReview() {
  log.info('[useClientReviews] useRecaptureClientReview called');
  return useApiMutation<Awaited<ReturnType<typeof clientReviewsApi.recapture>>, string>({
    mutationFn: (token, id) => clientReviewsApi.recapture(token, id),
    invalidateKeys: [['client-reviews']],
  });
}

export function useClientReviewVersions(reviewId: string, enabled = true) {
  return useApiQuery({ queryKey: ['client-review-versions', reviewId], queryFn: token => clientReviewsApi.versions(token, reviewId), enabled: enabled && !!reviewId, staleTime: 0, refetchInterval: enabled ? 10000 : false });
}
export function useCreateClientReviewVersion() {
  return useApiMutation<Awaited<ReturnType<typeof clientReviewsApi.createVersion>>, {reviewId:string;name:string;requestId:string;baseVersionId:string|null}>({mutationFn:(token,{reviewId,...body})=>clientReviewsApi.createVersion(token,reviewId,body),invalidateKeys:[['client-review-versions'],['client-reviews']]});
}
